import React, { useRef, useEffect, useState } from 'react';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import TechMarquee from '../../components/marquee/TechMarquee';
import ScrollProgress from '../../components/ScrollProgress/ScrollProgress';
import CTABanner from '../cta/CTABanner';
import HeroBulb from '../../components/hero/HeroBulb';
import hye_img from '../../assets/image/wave.webp';
import profile_img from '../../assets/image/obaid-560.webp';
import profile_img_sm from '../../assets/image/obaid-280.webp';
import profile_img_avif from '../../assets/image/obaid-560.avif';
import profile_img_avif_sm from '../../assets/image/obaid-280.avif';
import { TbBrandGithub, TbBrandLinkedin } from 'react-icons/tb';
import { MdOutlineEmail } from 'react-icons/md';
import { FiArrowDown } from 'react-icons/fi';
import About from '../about/About';
import Works from '../works/Works';
import Services from '../services/Services';
import Contact from '../contact/Contact';
import './Home.css';
import { Element, Link } from 'react-scroll';
import { motion, useInView } from 'framer-motion';
import Skills from '../skills/Skills';
import FAQ from '../faq/FAQ';
import CountUp from 'react-countup';

/* ── Typewriter hook ─────────────────────────── */
const ROLES = ['Full-Stack Engineer', 'AI / ML Engineer', 'LLM & RAG Systems Builder', 'Backend Architect'];
function useTypewriter(words, speed = 90, deleteSpeed = 55, pause = 1800) {
  const [text, setText] = useState('');
  const [idx, setIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[idx % words.length];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setText(current.slice(0, text.length + 1));
        if (text.length + 1 === current.length) setTimeout(() => setDeleting(true), pause);
      } else {
        setText(current.slice(0, text.length - 1));
        if (text.length - 1 === 0) { setDeleting(false); setIdx(i => i + 1); }
      }
    }, deleting ? deleteSpeed : speed);
    return () => clearTimeout(timeout);
  }, [text, deleting, idx, words, speed, deleteSpeed, pause]);

  return text;
}

/* ── Stat counter ────────────────────────────── */
const Stat = ({ value, suffix, label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <div className="stat-item" ref={ref}>
      <div className="stat-value">
        {inView ? <CountUp end={value} duration={2.2} suffix={suffix} /> : `0${suffix}`}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

const Home = () => {
  const role = useTypewriter(ROLES);
  const heroRef = useRef(null);
  const inView = useInView(heroRef, { once: true });

  return (
    <>
      <ScrollProgress />
      <Element name="home">
        <section id="home-section">
          <Header />
          <div className="container" id="home">
            <div className="hero-grid">
              {/* ── Left content ── */}
              <motion.div
                className="hero-left"
                ref={heroRef}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              >
                <p className="hero-greeting">
                  Hello, I'm&nbsp;<img src={hye_img} width="26" height="26" alt="wave emoji" className="wave-emoji" />
                </p>

                <h1 className="hero-name">Obaidullah<br />Zeeshan</h1>

                <p className="hero-role">
                  a <span className="role-highlight">{role}</span>
                  <span className="cursor-blink">|</span>
                </p>

                <p className="hero-tagline">
                  Building scalable full-stack systems and production AI/ML — LLM orchestration, RAG pipelines, and computer vision — on containerized, self-hosted infrastructure.
                </p>

                <p className="hero-mantra">&#8220;Space is the Limit, Sky is Overrated&#8221;</p>

                {/* Stats */}
                <div className="hero-stats">
                  <Stat value={3} suffix="+" label="Years Experience" />
                  <div className="stat-sep" />
                  <Stat value={32} suffix="+" label="Projects Worked" />
                  <div className="stat-sep" />
                  <Stat value={3} suffix="" label="Companies Worked" />
                </div>

                {/* CTAs */}
                <div className="hero-cta">
                  <a className="btn-primary" href="/documents/obaidullah-zeeshan-software-engineer.pdf" download="Obaidullah_Zeeshan_CV.pdf" aria-label="Download Obaidullah Zeeshan's CV">
                    Download CV
                  </a>
                  <Link className="btn-outline" to="contact" href="/#contact" smooth duration={500}>
                    Let's Talk
                  </Link>
                </div>

                {/* Social row */}
                <div className="hero-socials">
                  <motion.a
                    href="https://github.com/ObaidGits"
                    target="_blank" rel="noopener noreferrer"
                    className="hero-social" aria-label="GitHub"
                    whileHover={{ scale: 1.18, y: -3 }}
                    whileTap={{ scale: 0.88 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 18 }}
                  >
                    <TbBrandGithub />
                  </motion.a>
                  <motion.a
                    href="https://www.linkedin.com/in/obaidullah-zeeshan/"
                    target="_blank" rel="noopener noreferrer"
                    className="hero-social" aria-label="LinkedIn"
                    whileHover={{ scale: 1.18, y: -3 }}
                    whileTap={{ scale: 0.88 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 18 }}
                  >
                    <TbBrandLinkedin />
                  </motion.a>
                  <motion.a
                    href="mailto:obaidzeeshan.official@gmail.com"
                    className="hero-social" aria-label="Email"
                    title="obaidzeeshan.official@gmail.com"
                    whileHover={{ scale: 1.18, y: -3 }}
                    whileTap={{ scale: 0.88 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 18 }}
                  >
                    <MdOutlineEmail />
                  </motion.a>
                </div>
              </motion.div>

              {/* ── Right image ── */}
              <motion.div
                className="hero-right"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                <HeroBulb
                  profileImg={profile_img}
                  profileImgSm={profile_img_sm}
                  profileImgAvif={profile_img_avif}
                  profileImgAvifSm={profile_img_avif_sm}
                />
              </motion.div>
            </div>
          </div>

          {/* Scroll cue */}
          <div className="scroll-cue" aria-hidden="true">
            <FiArrowDown className="scroll-cue-arrow" />
          </div>
        </section>
      </Element>

      <TechMarquee />
      <div className="section-divider" />
      <Works />
      <div className="section-divider" />
      <Services />
      <div className="section-divider" />
      <About />
      <div className="section-divider" />
      <Skills />
      <div className="section-divider" />
      <FAQ />
      <div className="section-divider" />
      <CTABanner />
      <div className="section-divider" />
      <Contact />
      <Footer />
    </>
  );
};

export default Home;
